import styled, { css } from "styled-components";
import { premiumTheme as theme } from "./premiumTheme";

// Presentation only. State, validation, selection and portal ownership stay in the existing engines.
const premiumVariables = css`
    --text-icons-green-500: ${theme.mandatory};
    --text-and-icons-green-500: ${theme.mandatory};
    --Buttons-Green-Outline: ${theme.mandatory};
    --App-Accent-Outline: ${theme.mandatory};
`;
const valueTypography = css`
    font-family: ${theme.fontFamily}; font-size: ${theme.valueSize};
    font-weight: ${theme.valueWeight}; line-height: ${theme.valueLineHeight};
`;

// Respect the existing explicit sizing override, including compact and multiline consumers.
const fieldHeight = ({ formFieldHeight }) => formFieldHeight ?? theme.fieldHeight;
const premiumTagStyles = css`
    .category-tag-container { color: ${theme.mandatory} !important; background: ${theme.optionHover} !important; border-radius: 6px; }
`;

// Re-exported by PremiumMultiSelect for existing consumers during the staged migration.
export const premiumDropdownStyles = css`
    ${premiumVariables}
    ${premiumTagStyles}
    && .dropdown-overlay .property-1-variant-3 {
        background: ${theme.panelBackground}; border: 1px solid ${theme.fieldBorder};
        border-radius: ${theme.fieldRadius}; box-shadow: ${theme.panelShadow};
        padding: ${theme.panelPadding}; box-sizing: border-box;
    }
    && .dropdown-overlay .frame-48-dropdown {
        background: ${theme.fieldBackground} !important; border-color: ${theme.fieldBorder}; border-radius: 6px;
    }
    && .dropdown-overlay .frame-48-dropdown:focus-within { border-color: ${theme.mandatory}; }
    && .dropdown-overlay .search-input { ${valueTypography} color: ${theme.valueColor}; }
    && .dropdown-overlay .search-input::placeholder { color: ${theme.searchPlaceholder}; }
    && .dropdown-overlay .search-input:focus { outline: none; box-shadow: none; }
    && .dropdown-overlay .separator { background: ${theme.panelSeparator}; }
    && .dropdown-overlay .dropdown-item {
        background: ${theme.panelBackground}; border-radius: 5px; color: ${theme.valueColor}; min-height: 32px;
    }
    && .dropdown-overlay .dropdown-text { ${valueTypography} color: inherit; }
    && .dropdown-overlay .dropdown-item:not(.hover-not-allowed):hover { background: ${theme.optionHover}; }
    && .dropdown-overlay .dropdown-item:focus-visible { outline: 2px solid ${theme.mandatory}; outline-offset: -2px; }
    && .dropdown-overlay .dropdown-item.hover-not-allowed,
    && .dropdown-overlay .dropdown-item .locked { color: ${theme.panelMuted} !important; }
    && .dropdown-overlay .checkbox-standard { color: ${theme.mandatory} !important; }
    && .dropdown-overlay .checkbox-standard.disabled { color: ${theme.panelMuted} !important; }
    && .dropdown-overlay .btn-large {
        background: ${theme.actionBackground} !important; border: 1px solid ${theme.fieldBorder} !important;
        border-radius: 6px; color: ${theme.mandatory} !important;
    }
    && .dropdown-overlay .btn-large.enabled:hover { border-color: ${theme.mandatory} !important; }
    && .dropdown-overlay .btn-large:focus-visible { outline: 2px solid ${theme.mandatory}; outline-offset: 2px; }
    && .dropdown-overlay .btn-large.disabled { color: ${theme.panelMuted} !important; }
    && .dropdown-overlay .btn-large label { color: inherit !important; }
    && .dropdown-overlay .btn-large .Button-Text-Small {
        display: flex; align-items: center; justify-content: center; margin: 0; line-height: normal;
    }
    && .dropdown-overlay .dropdownlineitem-container {
        overscroll-behavior: contain; scrollbar-color: ${theme.fieldBorder} ${theme.fieldBackground};
    }
    && .dropdown-overlay .nothing-to-show-container { color: ${theme.panelEmpty}; }
`;

// This local wrapper travels with the actual overlay, including when rendered into document.body.
// Legacy FormField continues using a plain div; no document-wide stylesheet is installed.
export const PremiumDropdownSurface = styled.div`
    ${premiumDropdownStyles}
`;

export const premiumFormFieldStyles = css`
    &&&[data-appearance="premium"] {
        ${premiumVariables}
        ${premiumTagStyles}
        min-width: 0;
        .FormComponentContainer { min-width: 0; align-items: stretch; gap: 0; }
        .Frame51 { padding: 0; gap: 6px; margin-bottom: 8px; min-height: 14px; }
        .Title { font-size: 11px; font-weight: 500; text-transform: uppercase; }
        .Frame48 { min-height: ${fieldHeight} !important; min-width: 0; align-items: center; }
        .Frame48Input, .Frame48Input > label, .Frame48Input .category-tag-container {
            ${valueTypography}
            font-weight: ${theme.valueWeight} !important;
        }
        .Frame48Input {
            background: transparent !important; border-radius: ${theme.fieldRadius} !important;
            min-width: 0; box-sizing: border-box;
        }
        input.Frame48Input, div.Frame48Input { height: calc(${fieldHeight} - 2px) !important; }
        input.Frame48Input, textarea.Frame48Input { border: 0; outline: none !important; box-shadow: none !important; }
        .Frame48Input:focus-visible { outline: none; }
        .inputFieldContainer { align-items: center; }
        .Frame48LeftContent, .Frame48RightContent { height: auto; align-self: stretch; border-color: ${theme.panelSeparator}; }
        .dropdownArrow { top: 50%; transform: translateY(-50%); }
        .dropdownArrow.active { transform: translateY(-50%) rotate(180deg); }
        .Frame48.tagAsDropdown .dropdownArrow { transform: none; }
        .Frame48.tagAsDropdown .dropdownArrow.active { transform: rotate(180deg); }
        .Frame48Input::placeholder, label.placeholder { color: ${theme.placeholder} !important; font-style: italic; }
        .Frame50 {
            box-sizing: border-box; min-height: 20px; padding: 4px 0 0; margin: 0; width: 100%;
            min-width: 0; max-width: 100%; overflow: visible;
        }
        .Frame50.hidden { display: block !important; visibility: hidden; }
        .AssistiveText { height: auto; line-height: 1.4; white-space: normal; overflow-wrap: anywhere; }
        .Frame50.withCheckbox { align-items: center; gap: 4px; height: auto; }
        .Frame50.withCheckbox .AssistiveText {
            order: 0; flex: 1 1 auto; min-width: 0; line-height: ${theme.assistiveSize}; text-align: right;
        }
        .AssistiveTextCheckbox {
            order: 1; width: ${theme.assistiveSize}; height: ${theme.assistiveSize};
            flex: 0 0 ${theme.assistiveSize}; margin: 0;
        }
        .AssistiveTextCheckbox .checkbox-standard {
            width: ${theme.assistiveSize}; height: ${theme.assistiveSize} !important;
            display: flex; align-items: center; justify-content: center; line-height: 0;
        }
        .AssistiveTextCheckbox .main-checkbox { display: block; width: ${theme.assistiveSize}; height: ${theme.assistiveSize}; }
        .AssistiveTextCheckbox:focus-visible { outline: 2px solid ${theme.mandatory}; outline-offset: 1px; }
    }
    &&&[data-appearance="premium"][data-interaction-state="enabled"] {
        .Frame48:not(.invalid) .Frame48Input,
        .Frame48:not(.invalid) .Frame48Input > label:not(.placeholder),
        .Frame48:not(.invalid) .Frame48Input .category-tag-container { color: ${theme.valueColor} !important; }
        .Frame48:hover { border-color: ${theme.hoverBorder} !important; }
    }
    &&&[data-appearance="premium"][data-interaction-state="locked"],
    &&&[data-appearance="premium"][data-interaction-state="disabled"] {
        .Frame48 { background: ${theme.blockedBackground}; }
        .Frame48Input::placeholder, label.placeholder { color: ${theme.blockedPlaceholder} !important; }
    }
    &&&[data-appearance="premium"] {
        .Frame48:focus-within { border-color: ${theme.mandatory} !important; box-shadow: ${theme.focusShadow}; }
        .Frame48.invalid, .Frame48.invalid:hover, .Frame48.invalid:focus-within { border-color: ${theme.invalidBorder} !important; }
    }
    /* Locked is a presentation state, independent of requirement and disabled styling. */
    &&&[data-appearance="premium"][data-interaction-state="locked"] {
        .Frame48 .Frame48Input, .Frame48 .Frame48Input > label:not(.placeholder),
        .Frame48 .category-tag-container, .Frame48 .TagsInput, .Frame48 .textPrefix,
        .Frame48 .Frame48LeftContent, .Frame48 .Frame48RightContent {
            color: ${theme.lockedValue} !important;
        }
        .Frame48, .Frame48:hover, .Frame48:active, .Frame48:focus,
        .Frame48:focus-visible, .Frame48:focus-within {
            border-color: ${theme.fieldBorder} !important; outline: none !important; box-shadow: none !important;
        }
        .Frame48, .Frame48 * {
            cursor: not-allowed !important; outline: none !important; box-shadow: none !important;
        }
    }

    &&&&[data-appearance="premium"][data-value-presentation="suggested"] {
        .Frame48 .Frame48Input, .Frame48 .Frame48Input > label:not(.placeholder),
        .category-tag-container, .TagsInput, .textPrefix {
            color: ${theme.placeholder} !important;
            font-style: italic !important;
        }
    }
    &&[data-appearance="premium"] .premium-field-description {
        position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
        overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
    }
`;

export const premiumFormHeaderContent = css`
    display: flex; align-items: center; justify-content: flex-end;
    flex-wrap: wrap; gap: 12px; min-width: 0; max-width: 100%;
`;
export const premiumRequirementLegendStyles = css`
    display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
    min-width: 0; font-size: ${theme.legendSize};
    .premium-requirement-entry { display: flex; align-items: center; gap: 6px; }
    .premium-requirement-indicator { margin-left: 0; }
`;
